import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { checkIsAdmin, getAdminOverview, setVendorStatus, sendVendorPasswordReset, syncStripeSubscriptions, getCronSecret, updateCronSecret, getFacebookSettings, updateFacebookSettings, getFacebookPostLog, triggerFacebookPost, verifyFacebookToken, refreshFacebookToken } from "@/lib/admin.functions";
import { getNotFoundReport, resolveNotFound } from "@/lib/not-found.functions";
import { MailingSection } from "@/components/admin/MailingSection";
import { MailingCampaignsSection } from "@/components/admin/MailingCampaignsSection";
import { AuditLogSection } from "@/components/admin/AuditLogSection";
import { GscSection } from "@/components/admin/GscSection";
import { SeoDailySection } from "@/components/admin/SeoDailySection";
import { AutoArticlesSection } from "@/components/admin/AutoArticlesSection";

import { useAuth } from "@/lib/auth";
import { getAdminVisitStats, getAdminVisitBreakdown, getAdminContactStats, type VisitRange } from "@/lib/vendor-visits.functions";
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { LayoutDashboard, Store, Package, ShoppingBag, CreditCard, Users, Mail, Megaphone, History, FileWarning, Settings, LineChart, type LucideIcon } from "lucide-react";


const fmtInt = new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 0 }).format;
const fmtCurrency = new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR" }).format;

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin — kupish.si" }] }),
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => {
    const t = String(search.tab ?? "");
    return TABS.includes(t as Tab) ? { tab: t as Tab } : {};
  },
  component: Page,
});

type Tab = "overview" | "vendors" | "products" | "orders" | "subscriptions" | "mailing" | "campaigns" | "audit" | "notfound" | "settings" | "facebook" | "seo-dnevnik" | "samodejni-clanki";

const TABS: Tab[] = ["overview", "vendors", "products", "orders", "subscriptions", "mailing", "campaigns", "audit", "notfound", "settings", "facebook", "seo-dnevnik", "samodejni-clanki"];


function Page() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const checkAdmin = useServerFn(checkIsAdmin);
  const fetchOverview = useServerFn(getAdminOverview);
  const setStatus = useServerFn(setVendorStatus);
  const qc = useQueryClient();
  const search = Route.useSearch();
  const tab: Tab = search.tab ?? "overview";
  const setTab = (t: Tab) => navigate({ to: "/admin", search: t === "overview" ? {} : { tab: t }, replace: true });

  type VendorSortKey = "store_name" | "email" | "city" | "published_count" | "last_sign_in_at" | "status";
  const [vendorSort, setVendorSort] = useState<{ key: VendorSortKey; dir: "asc" | "desc" }>({ key: "store_name", dir: "asc" });
  const toggleVendorSort = (key: VendorSortKey) =>
    setVendorSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const adminCheck = useQuery({
    queryKey: ["admin-access", user?.id],
    queryFn: () => checkAdmin(),
    enabled: !!user,
    retry: false,
  });

  useEffect(() => {
    if (adminCheck.data?.isAdmin === false) {
      navigate({ to: "/", replace: true });
    }
  }, [adminCheck.data?.isAdmin, navigate]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-overview", user?.id],
    queryFn: () => fetchOverview(),
    enabled: !!user && adminCheck.data?.isAdmin === true,
    retry: false,
  });

  const isForbidden = !!error && /administrator|zavrnjen|forbidden|unauthorized/i.test((error as Error).message);

  useEffect(() => {
    if (isForbidden) {
      navigate({ to: "/", replace: true });
    }
  }, [isForbidden, navigate]);

  const statusMutation = useMutation({
    mutationFn: (vars: { vendor_id: string; status: "active" | "inactive" }) => setStatus({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-overview"] }),
  });

  const [resetMsg, setResetMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const sendResetFn = useServerFn(sendVendorPasswordReset);
  const syncSubscriptionsFn = useServerFn(syncStripeSubscriptions);
  const resetMutation = useMutation({
    mutationFn: (vars: { vendor_id: string }) => sendResetFn({ data: vars }),
    onSuccess: (data) => {
      setResetMsg({ type: "success", text: `Povezava za ponastavitev gesla poslana na ${data.email}.` });
      setTimeout(() => setResetMsg(null), 5000);
    },
    onError: (err: Error) => {
      setResetMsg({ type: "error", text: err.message });
      setTimeout(() => setResetMsg(null), 5000);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => syncSubscriptionsFn(),
    onSuccess: (result) => {
      setResetMsg({ type: "success", text: `Sinhronizacija zaključena: preverjenih ${result.checked}, posodobljenih ${result.synced} naročnin.` });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
      setTimeout(() => setResetMsg(null), 6000);
    },
    onError: (err: Error) => {
      setResetMsg({ type: "error", text: err.message });
      setTimeout(() => setResetMsg(null), 6000);
    },
  });

  if (adminCheck.isLoading || adminCheck.data?.isAdmin === false || isLoading || isForbidden) return <div className="mx-auto max-w-6xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  if (adminCheck.error) return <div className="mx-auto max-w-6xl px-6 py-24 text-destructive">Napaka: {(adminCheck.error as Error).message}</div>;
  if (error) return <div className="mx-auto max-w-6xl px-6 py-24 text-destructive">Napaka: {(error as Error).message}</div>;
  if (!data) return null;


  const groups: { title: string; items: { key: Tab; label: string; count?: number; icon: LucideIcon }[] }[] = [
    {
      title: "Pregled",
      items: [{ key: "overview", label: "Pregled in statistika", icon: LayoutDashboard }],
    },
    {
      title: "Trgovina",
      items: [
        { key: "vendors", label: "Prodajalci", count: data.vendors.length, icon: Store },
        { key: "products", label: "Izdelki", count: data.products.length, icon: Package },
        { key: "orders", label: "Naročila", count: data.orders.length, icon: ShoppingBag },
        { key: "subscriptions", label: "Naročnine", count: data.subscriptions.length, icon: CreditCard },
      ],
    },
    {
      title: "Trženje",
      items: [
        { key: "mailing", label: "Prejemniki", icon: Users },
        { key: "campaigns", label: "Kampanje", icon: Mail },
        { key: "facebook", label: "Facebook objave", icon: Megaphone },
      ],
    },
    {
      title: "Tehnika in dnevniki",
      items: [
        { key: "seo-dnevnik", label: "SEO dnevnik", icon: LineChart },
        { key: "samodejni-clanki", label: "Samodejni članki", icon: FileWarning },
        { key: "audit", label: "Dnevnik sprememb", icon: History },
        { key: "notfound", label: "404 strani", icon: FileWarning },
        { key: "settings", label: "Nastavitve", icon: Settings },
      ],
    },
  ];

  const allTabs = groups.flatMap((g) => g.items);
  const activeLabel = allTabs.find((t) => t.key === tab)?.label ?? "Pregled";

  return (
    <section className="w-full px-4 md:px-8 py-10 md:py-14">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Skrbniška plošča</div>
          <h1 className="font-serif text-3xl md:text-5xl mt-2">Admin</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/orodja" className="border border-brand text-brand px-4 py-2 text-sm hover:bg-brand/5 whitespace-nowrap">Strateška orodja →</Link>
          <Link to="/migracija-slik" className="border border-border px-4 py-2 text-sm hover:bg-muted whitespace-nowrap">Migracija slik → WebP</Link>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          {/* Mobilni izbirnik oddelka */}
          <div className="lg:hidden">
            <label htmlFor="admin-tab" className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Oddelek
            </label>
            <select
              id="admin-tab"
              value={tab}
              onChange={(e) => setTab(e.target.value as Tab)}
              className="w-full border border-border bg-background px-3 py-3 text-sm min-h-11"
            >
              {groups.map((g) => (
                <optgroup key={g.title} label={g.title}>
                  {g.items.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}{typeof t.count === "number" ? ` (${fmtInt(t.count)})` : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Namizni stranski meni */}
          <nav className="hidden lg:block space-y-7" aria-label="Skrbniška navigacija">
            {groups.map((g) => (
              <div key={g.title}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground">{g.title}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <ul className="divide-y divide-border/60">
                  {g.items.map((t) => {
                    const Icon = t.icon;
                    return (
                      <li key={t.key}>
                        <button
                          type="button"
                          onClick={() => setTab(t.key)}
                          aria-current={tab === t.key ? "page" : undefined}
                          className={`w-full text-left px-3 py-2.5 text-sm transition-colors border-l-2 ${
                            tab === t.key
                              ? "border-accent bg-muted text-foreground font-medium"
                              : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          }`}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-2 min-w-0">
                              <Icon className={`h-4 w-4 shrink-0 ${tab === t.key ? "text-accent" : "text-muted-foreground"}`} />
                              <span className="truncate">{t.label}</span>
                            </span>
                            {typeof t.count === "number" ? (
                              <span className="text-xs tabular-nums text-muted-foreground">{fmtInt(t.count)}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

        </aside>

      <div className="min-w-0">
        <h2 className="sr-only">{activeLabel}</h2>

        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Prodajalci" value={fmtInt(data.stats.vendors)} />
              <StatCard label="Izdelki" value={fmtInt(data.stats.products)} />
              <StatCard label="Naročila" value={fmtInt(data.stats.orders)} />
              <StatCard label="Prihodek" value={<span className="text-accent">{fmtCurrency(data.stats.revenue)}</span>} />
            </div>
            <div className="mt-8">
              <GscSection />
            </div>
            <div className="mt-8">
              <AdminVisitsChart />
            </div>

          </>
        )}


        {tab === "vendors" && (
          <>
            {resetMsg && (
              <div className={`mb-4 border px-4 py-3 text-sm ${resetMsg.type === "success" ? "border-green-300 bg-green-50 text-green-800" : "border-red-300 bg-red-50 text-red-800"}`}>
                {resetMsg.text}
              </div>
            )}
            <p className="text-xs text-muted-foreground mb-3">
              Geslo iz varnostnih razlogov ni vidno (shranjeno je samo enosmerno hashano). Uporabnik ga lahko ponastavi prek povezave »Pozabljeno geslo«.
            </p>
            {(() => {
              const cols: { key: VendorSortKey; label: string }[] = [
                { key: "store_name", label: "Trgovina" },
                { key: "email", label: "Uporabniško ime" },
                { key: "city", label: "Mesto" },
                { key: "published_count", label: "Objavljeni izdelki" },
                { key: "last_sign_in_at", label: "Zadnja aktivnost" },
                { key: "status", label: "Status" },
              ];
              const getVal = (v: typeof data.vendors[number], key: VendorSortKey): string | number => {
                const vv = v as typeof v & { email?: string | null; last_sign_in_at?: string | null; published_count?: number };
                switch (key) {
                  case "store_name": return (v.store_name || "").toLowerCase();
                  case "email": return (vv.email || "").toLowerCase();
                  case "city": return (v.city || "").toLowerCase();
                  case "published_count": return vv.published_count ?? 0;
                  case "last_sign_in_at": return vv.last_sign_in_at ? new Date(vv.last_sign_in_at).getTime() : 0;
                  case "status": return v.status || "";
                }
              };
              const sorted = [...data.vendors].sort((a, b) => {
                const av = getVal(a, vendorSort.key);
                const bv = getVal(b, vendorSort.key);
                if (av < bv) return vendorSort.dir === "asc" ? -1 : 1;
                if (av > bv) return vendorSort.dir === "asc" ? 1 : -1;
                return 0;
              });
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border">
                        {cols.map((c) => {
                          const active = vendorSort.key === c.key;
                          return (
                            <th key={c.key} className="py-3 pr-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                              <button
                                type="button"
                                onClick={() => toggleVendorSort(c.key)}
                                className="inline-flex items-center gap-1 hover:text-foreground"
                              >
                                {c.label}
                                <span className="text-[10px]">{active ? (vendorSort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                              </button>
                            </th>
                          );
                        })}
                        <th className="py-3 pr-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Dejanje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((v) => {
                        const vv = v as typeof v & { email?: string | null; last_sign_in_at?: string | null; published_count?: number };
                        return (
                          <tr key={v.id} className="border-b border-border">
                            <td className="py-3 pr-4">
                              <div className="font-medium">{v.store_name}</div>
                              <div className="text-xs text-muted-foreground">/prodajalci/{v.slug}</div>
                            </td>
                            <td className="py-3 pr-4 text-sm break-all">{vv.email || "—"}</td>
                            <td className="py-3 pr-4 text-sm">{v.city || "—"}</td>
                            <td className="py-3 pr-4 text-sm font-mono">{fmtInt(vv.published_count ?? 0)}</td>
                            <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">
                              {vv.last_sign_in_at
                                ? new Date(vv.last_sign_in_at).toLocaleString("sl-SI", { dateStyle: "short", timeStyle: "short" })
                                : "—"}
                            </td>
                            <td className="py-3 pr-4"><StatusPill status={v.status} /></td>
                            <td className="py-3 pr-4 space-y-1">
                              <button
                                disabled={statusMutation.isPending}
                                onClick={() => statusMutation.mutate({ vendor_id: v.id, status: v.status === "active" ? "inactive" : "active" })}
                                className="text-xs underline hover:text-accent disabled:opacity-50 block"
                              >
                                {v.status === "active" ? "Deaktiviraj" : "Aktiviraj"}
                              </button>
                              <button
                                disabled={resetMutation.isPending || !vv.email}
                                onClick={() => resetMutation.mutate({ vendor_id: v.id })}
                                className="text-xs underline hover:text-accent disabled:opacity-50 block"
                                title={vv.email ? "Pošlji povezavo za ponastavitev gesla" : "Ni e-naslova"}
                              >
                                {resetMutation.isPending && resetMutation.variables?.vendor_id === v.id ? "Pošiljam…" : "Ponastavi geslo"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </>
        )}

        {tab === "products" && (
          <Table headers={["Naziv", "Cena", "Status", "Zaloga"]}>
            {data.products.map((p) => (
              <tr key={p.id} className="border-b border-border">
                <td className="py-3 pr-4">{p.title}</td>
                <td className="py-3 pr-4">{p.price != null ? <span className="text-accent">{fmtCurrency(Number(p.price))}</span> : "—"}</td>
                <td className="py-3 pr-4"><StatusPill status={p.status} /></td>
                <td className="py-3 pr-4 text-sm">{p.in_stock ? "Na zalogi" : "Razprodano"}</td>
              </tr>
            ))}
          </Table>
        )}

        {tab === "orders" && (
          <Table headers={["Datum", "Kupec", "Znesek", "Status"]}>
            {data.orders.map((o) => (
              <tr key={o.id} className="border-b border-border">
                <td className="py-3 pr-4 text-sm">{new Date(o.created_at).toLocaleString("sl-SI")}</td>
                <td className="py-3 pr-4 text-sm">{o.customer_email}</td>
                <td className="py-3 pr-4 text-accent">{fmtCurrency(Number(o.amount_total))}</td>
                <td className="py-3 pr-4"><StatusPill status={o.status} /></td>
              </tr>
            ))}
          </Table>
        )}

        {tab === "subscriptions" && (
          <>
            {resetMsg && (
              <div className={`mb-4 border px-4 py-3 text-sm ${resetMsg.type === "success" ? "border-green-300 bg-green-50 text-green-800" : "border-red-300 bg-red-50 text-red-800"}`}>
                {resetMsg.text}
              </div>
            )}
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                disabled={syncMutation.isPending}
                onClick={() => syncMutation.mutate()}
                className="border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
              >
                {syncMutation.isPending ? "Sinhroniziram…" : "Sinhroniziraj iz Stripe"}
              </button>
            </div>
            <Table headers={["Kupec", "Paket", "Znesek", "Status", "Velja do", "Zadnja prijava"]}>
              {data.subscriptions.map((s) => {
                const last = (s as { last_sign_in_at?: string | null }).last_sign_in_at;
                return (
                  <tr key={s.id} className="border-b border-border">
                    <td className="py-3 pr-4 text-sm">{s.customer_email}</td>
                    <td className="py-3 pr-4 text-sm">{s.product_name || "—"}</td>
                    <td className="py-3 pr-4">{s.amount != null ? <span className="text-accent">{fmtCurrency(Number(s.amount))}</span> : "—"}</td>
                    <td className="py-3 pr-4"><StatusPill status={s.status} /></td>
                    <td className="py-3 pr-4 text-sm">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString("sl-SI") : "—"}</td>
                    <td className="py-3 pr-4 text-sm">{last ? new Date(last).toLocaleString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  </tr>
                );
              })}
            </Table>

          </>
        )}

        {tab === "mailing" && <MailingSection />}
        {tab === "campaigns" && <MailingCampaignsSection />}
        {tab === "seo-dnevnik" && <SeoDailySection />}
        {tab === "samodejni-clanki" && <AutoArticlesSection />}

        {tab === "audit" && <AuditLogSection />}
        {tab === "notfound" && <NotFoundSection />}
        {tab === "facebook" && <FacebookPostsSection />}
        {tab === "settings" && (
          <div className="space-y-12">
            <SettingsSection />
          </div>
        )}
      </div>
      </div>
    </section>

  );
}

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="border border-border p-6">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 font-serif text-3xl">{value}</div>
    </div>
  );
}

function Table({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-border">
            {headers.map((h) => (
              <th key={h} className="py-3 pr-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    paid: "bg-green-100 text-green-800",
    publish: "bg-green-100 text-green-800",
    trialing: "bg-blue-100 text-blue-800",
    pending: "bg-yellow-100 text-yellow-800",
    draft: "bg-yellow-100 text-yellow-800",
    quote_requested: "bg-yellow-100 text-yellow-800",
    quote_sent: "bg-blue-100 text-blue-800",
    shipped: "bg-green-100 text-green-800",
    accepted: "bg-green-100 text-green-800",
    rejected: "bg-muted text-muted-foreground",
    inactive: "bg-muted text-muted-foreground",
    canceled: "bg-muted text-muted-foreground",
    refunded: "bg-muted text-muted-foreground",
    failed: "bg-red-100 text-red-800",
    past_due: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded ${map[status] ?? "bg-muted text-muted-foreground"}`}>
      {status}
    </span>
  );
}

function AdminVisitsChart() {
  const [range, setRange] = useState<VisitRange>("1m");
  const [drillKind, setDrillKind] = useState<"site" | "shop" | "product" | "article" | null>(null);
  const fetchStats = useServerFn(getAdminVisitStats);
  const fetchBreakdown = useServerFn(getAdminVisitBreakdown);
  const fetchContact = useServerFn(getAdminContactStats);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-visits", range],
    queryFn: () => fetchStats({ data: { range } }),
    staleTime: 60_000,
  });
  const contact = useQuery({
    queryKey: ["admin-contact", range],
    queryFn: () => fetchContact({ data: { range } }),
    staleTime: 60_000,
  });
  const breakdown = useQuery({
    queryKey: ["admin-visits-breakdown", drillKind, range],
    queryFn: () => fetchBreakdown({ data: { kind: drillKind!, range } }),
    enabled: !!drillKind,
    staleTime: 60_000,
  });

  const ranges: { id: VisitRange; label: string }[] = [
    { id: "7d", label: "7 dni" },
    { id: "1m", label: "1 mesec" },
    { id: "3m", label: "3 meseci" },
    { id: "1y", label: "1 leto" },
    { id: "5y", label: "5 let" },
    { id: "all", label: "Od začetka" },
  ];

  const formatXAxis = (key: string) => {
    if (!data) return key;
    if (data.granularity === "day") {
      const d = new Date(key);
      return d.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit" });
    }
    const [y, m] = key.split("-");
    return `${m}/${y.slice(2)}`;
  };

  const totals = data?.totals ?? { site: 0, shop: 0, product: 0, article: 0 };
  const isEmpty = !data || (totals.site + totals.shop + totals.product + totals.article === 0);

  const drillLabels: Record<string, string> = {
    site: "Najbolj obiskane strani",
    shop: "Najbolj obiskane trgovine",
    product: "Najbolj obiskani izdelki",
    article: "Najbolj obiskani članki",
  };

  const toggleDrill = (k: "site" | "shop" | "product" | "article") =>
    setDrillKind((cur) => (cur === k ? null : k));

  return (
    <div className="border border-border p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Statistika obiskov</div>
          <h3 className="font-serif text-xl mt-1">Obiski po vrstah strani</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Kliknite kartico za podroben pregled najbolj obiskanih strani, trgovin, izdelkov ali člankov.
          </p>
        </div>
        <div className="flex gap-1 flex-wrap">
          {ranges.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              className={`px-3 py-1.5 text-xs border ${range === r.id ? "bg-foreground text-background border-foreground" : "border-border hover:border-accent"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Spletna stran" value={totals.site} color="hsl(var(--accent))" active={drillKind === "site"} onClick={() => toggleDrill("site")} />
        <StatBox label="Trgovine" value={totals.shop} color="#10b981" active={drillKind === "shop"} onClick={() => toggleDrill("shop")} />
        <StatBox label="Izdelki" value={totals.product} color="#f59e0b" active={drillKind === "product"} onClick={() => toggleDrill("product")} />
        <StatBox label="Članki" value={totals.article} color="#8b5cf6" active={drillKind === "article"} onClick={() => toggleDrill("article")} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatBox label="Klik e-pošte prodajalca" value={contact.data?.emailClicks ?? 0} color="#0ea5e9" />
        <StatBox label="Klik telefona prodajalca" value={contact.data?.phoneClicks ?? 0} color="#ef4444" />
        <StatBox label="Povpraševanja prek aplikacije" value={contact.data?.inquiries ?? 0} color="#a855f7" />
      </div>

      {drillKind && (
        <div className="border border-border bg-muted/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-serif text-lg">{drillLabels[drillKind]}</h4>
            <button
              type="button"
              onClick={() => setDrillKind(null)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Zapri ✕
            </button>
          </div>
          {breakdown.isLoading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Nalagam…</div>
          ) : !breakdown.data || breakdown.data.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Ni podatkov.</div>
          ) : (
            <ol className="divide-y divide-border">
              {breakdown.data.map((item, i) => (
                <li key={item.key} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-muted-foreground w-6 shrink-0">{i + 1}.</span>
                    <div className="min-w-0">
                      {item.href ? (
                        <a href={item.href} className="text-foreground hover:text-accent truncate block">
                          {item.label}
                        </a>
                      ) : (
                        <span className="truncate block">{item.label}</span>
                      )}

                      {item.sublabel && <div className="text-xs text-muted-foreground truncate">{item.sublabel}</div>}
                    </div>
                  </div>
                  <span className="font-serif text-base tabular-nums shrink-0">{fmtInt(item.count)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Nalagam…</div>
      ) : isEmpty ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border">
          V izbranem obdobju še ni obiskov.
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickFormatter={(v) => fmtInt(Number(v))} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                labelFormatter={formatXAxis}
                contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="site" name="Spletna stran" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.2} strokeWidth={2} />
              <Area type="monotone" dataKey="shop" name="Trgovine" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} />
              <Area type="monotone" dataKey="product" name="Izdelki" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} />
              <Area type="monotone" dataKey="article" name="Članki" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color, active, onClick }: { label: string; value: number; color: string; active?: boolean; onClick?: () => void }) {
  const Comp: any = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`border p-4 text-left transition-colors ${onClick ? "hover:border-accent cursor-pointer" : ""} ${active ? "border-accent bg-accent/5" : "border-border"}`}
    >
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      <div className="font-serif text-2xl mt-1">{fmtInt(value)}</div>
      {onClick && <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{active ? "Skrij podrobnosti" : "Prikaži podrobnosti →"}</div>}
    </Comp>
  );
}



function NotFoundSection() {
  const fetchReport = useServerFn(getNotFoundReport);
  const resolveFn = useServerFn(resolveNotFound);
  const qc = useQueryClient();
  const [showResolved, setShowResolved] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-notfound", showResolved],
    queryFn: () => fetchReport({ data: { includeResolved: showResolved } }),
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (vars: { id: string; resolved: boolean; note?: string }) => resolveFn({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-notfound"] });
      setEditingId(null);
      setDraftNote("");
    },
  });

  const rows = data?.rows ?? [];
  const total = rows.reduce((s, r) => s + (r.hit_count ?? 0), 0);

  const startEdit = (r: typeof rows[number]) => {
    setEditingId(r.id);
    setDraftNote(r.note ?? "");
  };

  const saveNote = (id: string, resolved: boolean) => {
    mutation.mutate({ id, resolved, note: draftNote });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">SEO higiena</div>
          <h3 className="font-serif text-2xl mt-1">404 strani</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Zabeleženi zadetki na neobstoječe URL-je. Dodajte pravilo v <code className="px-1 bg-muted">src/lib/legacy-redirects.ts</code> in označite kot rešeno. V opombo zapišite ciljno preusmeritev.
          </p>
          <p className="text-xs text-muted-foreground mt-1">{fmtInt(rows.length)} unikatnih poti · {fmtInt(total)} zadetkov skupaj</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Prikaži rešene
        </label>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Nalagam…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border">
          Brez zabeleženih 404 strani. 🎉
        </div>
      ) : (
        <Table headers={["Pot", "Zadetki", "Zadnjič", "Vir", "Opomba / preusmeritev", "Dejanje"]}>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border align-top">
              <td className="py-3 pr-4 text-sm">
                <a href={r.path} target="_blank" rel="noreferrer" className="underline hover:text-accent break-all">{r.path}</a>
                {r.resolved && <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">rešeno</span>}
              </td>
              <td className="py-3 pr-4 font-mono text-sm">{fmtInt(r.hit_count)}</td>
              <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">
                {new Date(r.last_seen_at).toLocaleString("sl-SI", { dateStyle: "short", timeStyle: "short" })}
              </td>
              <td className="py-3 pr-4 text-xs text-muted-foreground break-all max-w-xs">
                {r.last_referrer || "—"}
              </td>
              <td className="py-3 pr-4 text-sm">
                {editingId === r.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={draftNote}
                      onChange={(e) => setDraftNote(e.target.value)}
                      placeholder="npr. /kategorije/moda"
                      className="w-full px-2 py-1 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-accent"
                      maxLength={500}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveNote(r.id, r.resolved)}
                        disabled={mutation.isPending}
                        className="text-xs underline hover:text-accent disabled:opacity-50"
                      >
                        Shrani
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setDraftNote(""); }}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Prekliči
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-muted-foreground break-all">{r.note || "—"}</span>
                    <button
                      onClick={() => startEdit(r)}
                      className="text-xs underline hover:text-accent whitespace-nowrap"
                      title="Uredi opombo"
                    >
                      uredi
                    </button>
                  </div>
                )}
              </td>
              <td className="py-3 pr-4">
                <button
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ id: r.id, resolved: !r.resolved, note: r.note ?? undefined })}
                  className="text-xs underline hover:text-accent disabled:opacity-50 whitespace-nowrap"
                >
                  {r.resolved ? "Ponovno odpri" : "Označi rešeno"}
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function SettingsSection() {
  const getSecret = useServerFn(getCronSecret);
  const updateSecret = useServerFn(updateCronSecret);
  const qc = useQueryClient();

  const { data: secretData } = useQuery({
    queryKey: ["cron-secret"],
    queryFn: () => getSecret(),
  });

  const [secretInput, setSecretInput] = useState("");
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (s: string) => updateSecret({ data: { secret: s } }),
    onSuccess: () => {
      setMsg({ type: "success", text: "CRON geslo uspešno posodobljeno." });
      setSecretInput("");
      qc.invalidateQueries({ queryKey: ["cron-secret"] });
      setTimeout(() => setMsg(null), 4000);
    },
    onError: (err: Error) => {
      setMsg({ type: "error", text: err.message });
      setTimeout(() => setMsg(null), 4000);
    },
  });

  return (
    <div className="max-w-xl">
      <h3 className="font-serif text-2xl">Cron nastavitve</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Geslo za avtentikacijo cron jobov. Če je prazno, cron jobi delujejo z anon ključem.
      </p>

      {msg && (
        <div className={`mt-4 border px-4 py-3 text-sm ${msg.type === "success" ? "border-green-300 bg-green-50 text-green-800" : "border-red-300 bg-red-50 text-red-800"}`}>
          {msg.text}
        </div>
      )}

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Trenutno geslo</label>
          <div className="px-3 py-2 border border-border bg-muted rounded text-sm font-mono">
            {secretData?.masked || "Ni nastavljeno"}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Novo geslo</label>
          <input
            type="password"
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
            placeholder="Vnesi novo CRON geslo..."
            className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Min. 1 znak, max. 256 znakov. Priporočamo naključno generirano geslo (npr. 32+ znakov).
          </p>
        </div>

        <button
          onClick={() => mutation.mutate(secretInput)}
          disabled={!secretInput || mutation.isPending}
          className="px-4 py-2 bg-accent text-accent-foreground text-sm rounded hover:opacity-90 disabled:opacity-50"
        >
          {mutation.isPending ? "Shranjujem..." : "Shrani CRON geslo"}
        </button>
      </div>
    </div>
  );
}

function FacebookPostsSection() {
  const getFb = useServerFn(getFacebookSettings);
  const updateFb = useServerFn(updateFacebookSettings);
  const getLog = useServerFn(getFacebookPostLog);
  const postNow = useServerFn(triggerFacebookPost);
  const verifyToken = useServerFn(verifyFacebookToken);
  const qc = useQueryClient();

  const { data: fb } = useQuery({
    queryKey: ["fb-settings"],
    queryFn: () => getFb(),
  });
  const { data: log } = useQuery({
    queryKey: ["fb-post-log"],
    queryFn: () => getLog(),
  });

  const [pageId, setPageId] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const [msg, setMsg] = useState<{ type: "success" | "error" | "warning"; text: string } | null>(null);
  const [tokenCheck, setTokenCheck] = useState<
    { valid: boolean; message: string; type: string | null; scopes: string[]; expires_at: string | null; never_expires: boolean; page_access: boolean; page_name: string | null } | null
  >(null);

  const formatCheck = (c: NonNullable<typeof tokenCheck>) => {
    const parts: string[] = [];
    parts.push(c.valid ? "Token je veljaven." : `Token ni veljaven: ${c.message}`);
    if (c.valid) {
      if (c.never_expires) parts.push("Ne poteče (trajni Page token).");
      else if (c.expires_at)
        parts.push(
          `Poteče ${new Date(c.expires_at).toLocaleString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}.`
        );
      if (c.type) parts.push(`Vrsta: ${c.type}.`);
      parts.push(c.page_access ? `Dostop do strani: ${c.page_name ?? "da"}.` : `Ni dostopa do strani. ${c.message}`);
    }
    return parts.join(" ");
  };

  const verifyMutation = useMutation({
    mutationFn: () => verifyToken(),
    onSuccess: (c) => {
      setTokenCheck(c);
      setMsg({ type: c.valid && c.page_access ? "success" : c.valid ? "warning" : "error", text: formatCheck(c) });
    },
    onError: (err: Error) => {
      setTokenCheck(null);
      setMsg({ type: "error", text: err.message });
    },
  });

  const refreshFn = useServerFn(refreshFacebookToken);
  const refreshMutation = useMutation({
    mutationFn: () => refreshFn(),
    onSuccess: (res) => {
      const r = res as { expires_at?: string | null };
      setMsg({
        type: "success",
        text: `Token je podaljšan${r.expires_at ? ` — poteče ${new Date(r.expires_at).toLocaleDateString("sl-SI")}` : " (trajni Page token)"}.`,
      });

      qc.invalidateQueries({ queryKey: ["fb-settings"] });
    },
    onError: (err: Error) => setMsg({ type: "error", text: err.message }),
  });

  useEffect(() => {
    if (fb) {
      setPageId(fb.page_id ?? "");
      setEmail(fb.contact_email ?? "");
      setExpiresAt(fb.token_expires_at ?? "");
      setAppId(fb.app_id ?? "");
    }
  }, [fb]);


  const expiryWarning =
    fb && fb.days_until_expiry !== null && fb.days_until_expiry !== undefined && fb.days_until_expiry <= 7
      ? `Token potek${fb.days_until_expiry <= 0 ? "el" : `e čez ${fb.days_until_expiry} dni`}.`
      : null;

  const updateMutation = useMutation({
    mutationFn: () =>
      updateFb({
        data: {
          page_id: pageId.trim(),
          contact_email: email.trim(),
          access_token: token.trim() || undefined,
          token_expires_at: expiresAt.trim() || undefined,
          app_id: appId.trim(),
          app_secret: appSecret.trim() || undefined,

        },
      }),
    onSuccess: (res) => {
      const c = (res as { check?: typeof tokenCheck }).check ?? null;
      setTokenCheck(c);
      setMsg({
        type: c && !c.page_access ? "warning" : "success",
        text: c ? `Facebook nastavitve so shranjene. ${formatCheck(c)}` : "Facebook nastavitve so shranjene.",
      });
      setToken("");
      setAppSecret("");

      qc.invalidateQueries({ queryKey: ["fb-settings"] });
      setTimeout(() => setMsg(null), 4000);
    },
    onError: (err: Error) => {
      setMsg({ type: "error", text: err.message });
      setTimeout(() => setMsg(null), 6000);
    },
  });

  const postMutation = useMutation({
    mutationFn: () => postNow(),
    onSuccess: (result) => {
      if ("skipped" in result && result.skipped) {
        setMsg({ type: "success", text: "Ni razpoložljivih člankov za objavo." });
      } else {
        setMsg({
          type: "success",
          text: `Objavljeno na Facebook: ${(result as { title: string }).title}`,
        });
      }
      qc.invalidateQueries({ queryKey: ["fb-post-log"] });
      setTimeout(() => setMsg(null), 6000);
    },
    onError: (err: Error) => {
      setMsg({ type: "error", text: err.message });
      setTimeout(() => setMsg(null), 6000);
    },
  });

  return (
    <div className="space-y-12">
      <div>
        <h3 className="font-serif text-2xl">Facebook objave</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Pregled objav na Facebook stran in ročna objava naslednjega članka. Avtomatske objave potekajo vsak dan ob 9:00.
        </p>

        {msg && (
          <div className={`mt-4 border px-4 py-3 text-sm ${
            msg.type === "success"
              ? "border-green-300 bg-green-50 text-green-800"
              : msg.type === "warning"
              ? "border-yellow-300 bg-yellow-50 text-yellow-800"
              : "border-red-300 bg-red-50 text-red-800"
          }`}>
            {msg.text}
          </div>
        )}

        {expiryWarning && (
          <div className="mt-4 border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            ⚠️ {expiryWarning} Podaljšaj ga v nastavitvah spodaj, sicer bodo avtomatske objave prenehale.
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <button
            onClick={() => postMutation.mutate()}
            disabled={postMutation.isPending}
            className="px-4 py-2 bg-accent text-accent-foreground text-sm rounded hover:opacity-90 disabled:opacity-50"
          >
            {postMutation.isPending ? "Objavljam…" : "Objavi naslednji članek zdaj"}
          </button>
          <p className="text-xs text-muted-foreground">
            Izberemo najstarejši neobjavljen članek iz kategorij Navdih & zgodbe.
          </p>
        </div>
      </div>

      <div>
        <h4 className="font-serif text-xl mb-4">Dnevnik objav</h4>
        {log && log.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 pr-4">Članek</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Datum objave</th>
                  <th className="py-2 pr-4">FB ID</th>
                </tr>
              </thead>
              <tbody>
                {log.map((row) => (
                  <tr key={`${row.article_slug}-${row.posted_at}`} className="border-b border-border align-top">
                    <td className="py-3 pr-4">
                      <Link to="/clanek/$slug" params={{ slug: row.article_slug }} className="hover:text-accent underline underline-offset-2">
                        {row.article_slug}
                      </Link>
                    </td>
                    <td className="py-3 pr-4">
                      {row.status === "success" ? (
                        <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                          Uspešno
                        </span>
                      ) : (
                        <span className="inline-block rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                          Neuspešno
                        </span>
                      )}
                      {row.attempt_count > 1 && (
                        <span className="block text-xs text-muted-foreground mt-1">
                          poskusov: {row.attempt_count}
                        </span>
                      )}
                      {row.status !== "success" && row.error_message && (
                        <span className="block text-xs text-muted-foreground mt-1 max-w-xs break-words">
                          {row.error_message}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {new Date(row.posted_at).toLocaleString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs">
                      {row.fb_post_id ? (
                        <a
                          href={`https://www.facebook.com/${row.fb_post_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-2 hover:text-accent break-all"
                          title="Odpri objavo na Facebooku"
                        >
                          {row.fb_post_id}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Še ni objav.</p>
        )}
      </div>

      <div className="max-w-xl border-t border-border pt-10">
        <h4 className="font-serif text-xl">Nastavitve Facebook strani</h4>
        <p className="text-sm text-muted-foreground mt-1">
          Podatki za povezavo s Facebook stranjo. Access token je občutljiv — shranjen je varno v bazi in nikoli prikazan v celoti.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Facebook Page ID</label>
            <input
              type="text"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              placeholder="npr. 122045229133"
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Kontaktni e-naslov</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="npr. info@kupish.si"
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Za obvestila in odgovore povezane s FB stranjo.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Trenutni access token</label>
            <div className="px-3 py-2 border border-border bg-muted rounded text-sm font-mono">
              {fb?.access_token_masked || "Ni nastavljen"}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Veljavnost tokena</label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Datum izteka tokena. Avtomatski opomnik po e-pošti se pošlje 2 dni prej.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Nov access token</label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Prilepi Facebook Page Access Token..."
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Ob shranjevanju token samodejno preverimo pri Facebooku; neveljaven se ne shrani, datum poteka pa se zapiše samodejno. Potrebna dovoljenja:
              <code className="mx-1">pages_manage_posts</code>,
              <code className="mx-1">pages_read_engagement</code>.
            </p>
          </div>

          <div className="border-t border-border pt-4">
            <label className="block text-sm font-medium mb-1">Facebook App ID</label>
            <input
              type="text"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="npr. 1234567890123456"
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Facebook App Secret</label>
            <div className="px-3 py-2 border border-border bg-muted rounded text-sm font-mono mb-2">
              {fb?.app_secret_masked || "Ni nastavljen"}
            </div>
            <input
              type="password"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder="Prilepi App Secret (Meta for Developers → Settings → Basic)"
              className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-accent"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Z App ID in App Secret lahko sistem sam podaljša token na 60 dni in nato pridobi trajni Page Access Token — ročno vnašanje tokena ni več potrebno.
            </p>
          </div>


          {tokenCheck && (
            <div
              className={`border px-3 py-2 text-sm ${
                tokenCheck.valid && tokenCheck.page_access
                  ? "border-green-300 bg-green-50 text-green-800"
                  : tokenCheck.valid
                  ? "border-yellow-300 bg-yellow-50 text-yellow-800"
                  : "border-red-300 bg-red-50 text-red-800"
              }`}
            >
              {formatCheck(tokenCheck)}
              {tokenCheck.scopes.length > 0 && (
                <span className="block text-xs mt-1 break-words">Dovoljenja: {tokenCheck.scopes.join(", ")}</span>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || (!pageId && !email && !token && !expiresAt && !appId && !appSecret)}
            className="px-4 py-2 bg-accent text-accent-foreground text-sm rounded hover:opacity-90 disabled:opacity-50"
          >
            {updateMutation.isPending ? "Shranjujem..." : "Shrani nastavitve"}
          </button>
          <button
            onClick={() => verifyMutation.mutate()}
            disabled={verifyMutation.isPending || !fb?.has_access_token}
            className="px-4 py-2 border border-border text-sm rounded hover:bg-muted disabled:opacity-50"
          >
            {verifyMutation.isPending ? "Preverjam…" : "Preveri shranjeni token"}
          </button>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending || !fb?.has_access_token || !fb?.has_app_secret}
            className="px-4 py-2 border border-border text-sm rounded hover:bg-muted disabled:opacity-50"
            title={!fb?.has_app_secret ? "Najprej vnesite App ID in App Secret." : undefined}
          >
            {refreshMutation.isPending ? "Podaljšujem…" : "Podaljšaj token"}
          </button>
          </div>

        </div>
      </div>
    </div>
  );
}

